import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Loader,
  AlertCircle,
  Trophy,
  Award,
  ShieldCheck,
  Search,
  Trash2,
  ChevronLeft,
  ChevronRight,
  BadgeCheck,
  UserSearch,
  X,
  Settings2,
  ShieldOff,
  Info,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { eventTeamApiService, EventTeamApiError } from '../../../services/eventTeamApi';
import { usernameCacheService } from '../../../services/usernameCache';
import type {
  EventPointsEntry,
  EventPointsStats,
  EventPointsTotal,
  EventPrefixEligibilityRow,
  EventPrefixGrant,
  EventPrefixRequirement,
  PlayerPrefixesResponse,
  Prefix,
  PrefixWithRequirement,
  PrefixHolding,
  EventPointsMemberSummary,
} from '../../../types/eventTeam';
import type { EventTeamPerms } from '../../../utils/eventTeamPermissions';
import { prefixPlainText } from '../../../utils/prefixText';
import { PrefixBadge } from '../PrefixBadge';
import { AwardPointsModal } from '../AwardPointsModal';
import { GrantPrefixModal } from '../GrantPrefixModal';
import { PrefixRequirementModal } from '../PrefixRequirementModal';
import { ConfirmationModal } from '../../ConfirmationModal';
import { PlayerProfile } from '../../PlayerProfile';
import { CreatorProfile } from '../../CreatorProfile';
import { PlayerSearchField, type PlayerSelection } from '../PlayerSearchField';
import { cn } from '../../../utils/cn';
import { SectionHeader } from '../../ui/SectionHeader';
import { EmptyState } from '../../ui/EmptyState';
import { StatTile } from '../../ui/StatTile';
import { SkeletonLoader } from '../../SkeletonLoader';

type SubTab = 'leaderboard' | 'ledger' | 'catalog' | 'eligibility' | 'grants' | 'player';

const SUB_TABS: { id: SubTab; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
  { id: 'ledger', label: 'Points history', icon: Award },
  { id: 'catalog', label: 'Prefix Catalog', icon: ShieldCheck },
  { id: 'eligibility', label: 'Eligibility', icon: BadgeCheck },
  { id: 'grants', label: 'Grants', icon: ShieldCheck },
  { id: 'player', label: 'Find a player', icon: UserSearch },
];

const ENTRY_TYPE_CHIP: Record<string, string> = {
  award: 'bg-green-500/15 text-green-300 border-green-500/25',
  deduction: 'bg-red-500/15 text-red-300 border-red-500/25',
  adjustment: 'bg-amber-500/15 text-amber-300 border-amber-500/25',
  prefix_redemption: 'bg-violet-500/15 text-violet-300 border-violet-500/25',
};

const LEDGER_PAGE_SIZE = 50;

export interface EventPointsPrefixesPanelProps {
  perms: EventTeamPerms;
}

/**
 * Points & Prefixes Board (guide §1.8): "go on the board and see how many points
 * they have earned". Leaderboard, manager-only award/corrections, read-only prefix
 * catalog with requirements overlay, eligibility → grant → revoke, player lookup.
 */
export const EventPointsPrefixesPanel = ({ perms }: EventPointsPrefixesPanelProps) => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const subTab = SUB_TABS.find((t) => t.id === params.get('reward_view'))?.id ?? 'player';
  const setSubTab = (tab: SubTab) => setParams((current) => { const next = new URLSearchParams(current); next.set('reward_view', tab); return next; });

  // ── Leaderboard ─────────────────────────────────────────────────────────
  const [leaderboard, setLeaderboard] = useState<EventPointsTotal[] | null>(null);
  const [leaderboardError, setLeaderboardError] = useState<string | null>(null);
  const [pointsStats, setPointsStats] = useState<EventPointsStats | null>(null);
  const [memberSummary, setMemberSummary] = useState<EventPointsMemberSummary | null>(null);
  const [memberSummaryLoading, setMemberSummaryLoading] = useState(false);

  // ── Ledger ──────────────────────────────────────────────────────────────
  const [ledger, setLedger] = useState<EventPointsEntry[]>([]);
  const [ledgerTotal, setLedgerTotal] = useState(0);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerTypeFilter, setLedgerTypeFilter] = useState('');
  const [ledgerEventFilter, setLedgerEventFilter] = useState('');
  const [ledgerError, setLedgerError] = useState<string | null>(null);
  const [entryToDelete, setEntryToDelete] = useState<EventPointsEntry | null>(null);
  const [deletingEntry, setDeletingEntry] = useState(false);

  // ── Catalog ─────────────────────────────────────────────────────────────
  const [prefixes, setPrefixes] = useState<Prefix[]>([]);
  const [prefixTotal, setPrefixTotal] = useState(0);
  const [prefixPage, setPrefixPage] = useState(1);
  const [prefixSearch, setPrefixSearch] = useState('');
  const [prefixCategory, setPrefixCategory] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [requirements, setRequirements] = useState<EventPrefixRequirement[]>([]);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [detailPrefix, setDetailPrefix] = useState<PrefixWithRequirement | null>(null);
  const [detailHolders, setDetailHolders] = useState<PrefixHolding[] | null>(null);
  const [detailGrants, setDetailGrants] = useState<EventPrefixGrant[] | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // ── Eligibility ─────────────────────────────────────────────────────────
  const [eligibility, setEligibility] = useState<EventPrefixEligibilityRow[]>([]);
  const [eligibilityPrefixFilter, setEligibilityPrefixFilter] = useState('');
  const [eligibilityError, setEligibilityError] = useState<string | null>(null);

  // ── Grants ──────────────────────────────────────────────────────────────
  const [grants, setGrants] = useState<EventPrefixGrant[]>([]);
  const [grantsTotal, setGrantsTotal] = useState(0);
  const [grantsPage, setGrantsPage] = useState(1);
  const [grantsStatusFilter, setGrantsStatusFilter] = useState<'active' | 'revoked' | ''>('');
  const [grantsError, setGrantsError] = useState<string | null>(null);
  const [grantToRevoke, setGrantToRevoke] = useState<EventPrefixGrant | null>(null);
  const [revoking, setRevoking] = useState(false);

  // ── Player lookup ───────────────────────────────────────────────────────
  const [lookupPlayer, setLookupPlayer] = useState<PlayerSelection | null>(null);
  const [lookupResult, setLookupResult] = useState<PlayerPrefixesResponse | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const lookupRequest = useRef(0);
  /** "Apply prefix" from the lookup result (works for non-team players — Roll's request). */
  const [lookupGrantPrefixId, setLookupGrantPrefixId] = useState('');
  const [lookupGrantOptions, setLookupGrantOptions] = useState<Prefix[] | null>(null);

  // ── Ledger member filter (username picker) ──────────────────────────────
  const [ledgerMemberPlayer, setLedgerMemberPlayer] = useState<PlayerSelection | null>(null);

  // ── Modals ──────────────────────────────────────────────────────────────
  const [showAward, setShowAward] = useState(false);
  const [awardUuid, setAwardUuid] = useState<string | undefined>();
  const [grantTarget, setGrantTarget] = useState<{ prefix: Prefix; requirement: EventPrefixRequirement | null; presetUuid?: string } | null>(null);
  const [requirementTarget, setRequirementTarget] = useState<{ prefix: Prefix; requirement: EventPrefixRequirement | null } | null>(null);

  const [loading, setLoading] = useState(false);

  const requirementFor = useCallback(
    (prefixId: number): EventPrefixRequirement | null =>
      requirements.find((r) => r.prefix_id === prefixId) ?? null,
    [requirements],
  );

  // ── Loaders ─────────────────────────────────────────────────────────────

  const loadLeaderboard = useCallback(async () => {
    setLoading(true);
    setLeaderboardError(null);
    try {
      const [board, stats] = await Promise.all([
        eventTeamApiService.getPointsLeaderboard(),
        eventTeamApiService.getPointsStats().catch(() => null),
      ]);
      setLeaderboard(board);
      setPointsStats(stats);
      usernameCacheService.prefetchUsernames(board.map((r) => r.member_uuid));
    } catch (err) {
      setLeaderboardError(err instanceof Error ? err.message : 'Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLedger = useCallback(
    async (page: number) => {
      setLoading(true);
      setLedgerError(null);
      try {
        const res = await eventTeamApiService.getPointsLedger({
          member_uuid: ledgerMemberPlayer?.uuid || undefined,
          event_id: ledgerEventFilter.trim() || undefined,
          entry_type: (ledgerTypeFilter || undefined) as EventPointsEntry['entry_type'] | undefined,
          page,
          limit: LEDGER_PAGE_SIZE,
        });
        setLedger(res.results);
        setLedgerTotal(res.total);
        setLedgerPage(page);
        usernameCacheService.prefetchUsernames(res.results.map((e) => e.member_uuid));
      } catch (err) {
        setLedgerError(err instanceof Error ? err.message : 'Failed to load ledger');
      } finally {
        setLoading(false);
      }
    },
    [ledgerMemberPlayer, ledgerTypeFilter, ledgerEventFilter],
  );

  const loadCatalog = useCallback(
    async (page: number) => {
      setLoading(true);
      setCatalogError(null);
      try {
        const [catalog, cats, reqs] = await Promise.all([
          eventTeamApiService.listPrefixes({
            category: prefixCategory || undefined,
            search: prefixSearch.trim() || undefined,
            page,
            limit: 50,
          }),
          eventTeamApiService.listPrefixCategories().catch(() => [] as string[]),
          eventTeamApiService.listPrefixRequirements().catch(() => [] as EventPrefixRequirement[]),
        ]);
        // In-game ordering: priority ascending (guide §2.4).
        setPrefixes([...catalog.results].sort((a, b) => a.priority - b.priority));
        setPrefixTotal(catalog.total);
        setPrefixPage(page);
        setCategories(cats);
        setRequirements(reqs);
      } catch (err) {
        setCatalogError(err instanceof Error ? err.message : 'Failed to load prefix catalog');
      } finally {
        setLoading(false);
      }
    },
    [prefixCategory, prefixSearch],
  );

  const loadEligibility = useCallback(async () => {
    setLoading(true);
    setEligibilityError(null);
    try {
      const [rows, reqs] = await Promise.all([
        eventTeamApiService.getPrefixEligibility(
          eligibilityPrefixFilter ? Number(eligibilityPrefixFilter) : undefined,
        ),
        eventTeamApiService.listPrefixRequirements().catch(() => [] as EventPrefixRequirement[]),
      ]);
      setEligibility(rows);
      setRequirements(reqs);
      usernameCacheService.prefetchUsernames(rows.map((r) => r.member_uuid));
    } catch (err) {
      setEligibilityError(err instanceof Error ? err.message : 'Failed to load eligibility');
    } finally {
      setLoading(false);
    }
  }, [eligibilityPrefixFilter]);

  const loadGrants = useCallback(
    async (page: number) => {
      setLoading(true);
      setGrantsError(null);
      try {
        const res = await eventTeamApiService.listAllPrefixGrants({
          status: grantsStatusFilter || undefined,
          page,
          limit: 50,
        });
        setGrants(res.results);
        setGrantsTotal(res.total);
        setGrantsPage(page);
        usernameCacheService.prefetchUsernames(res.results.map((g) => g.member_uuid));
      } catch (err) {
        setGrantsError(err instanceof Error ? err.message : 'Failed to load grants');
      } finally {
        setLoading(false);
      }
    },
    [grantsStatusFilter],
  );

  useEffect(() => {
    if (subTab === 'leaderboard') void loadLeaderboard();
    else if (subTab === 'ledger') void loadLedger(1);
    else if (subTab === 'catalog') void loadCatalog(1);
    else if (subTab === 'eligibility') void loadEligibility();
    else if (subTab === 'grants') void loadGrants(1);
  }, [subTab, loadLeaderboard, loadLedger, loadCatalog, loadEligibility, loadGrants]);

  // ── Actions ─────────────────────────────────────────────────────────────

  const openMemberSummary = async (uuid: string) => {
    setMemberSummaryLoading(true);
    setMemberSummary(null);
    try {
      const summary = await eventTeamApiService.getMemberPointsSummary(uuid);
      setMemberSummary(summary);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to load player history');
    } finally {
      setMemberSummaryLoading(false);
    }
  };

  const openPrefixDetail = async (prefix: Prefix) => {
    setDetailLoading(true);
    setDetailHolders(null);
    setDetailGrants(null);
    try {
      const [full, holders, grantHistory] = await Promise.all([
        eventTeamApiService.getPrefix(prefix.id).catch(() => ({ ...prefix, requirement: requirementFor(prefix.id) })),
        eventTeamApiService.getPrefixHolders(prefix.id, 1, 100).catch(() => ({ results: [], total: 0 })),
        eventTeamApiService.getPrefixGrants(prefix.id).catch(() => [] as EventPrefixGrant[]),
      ]);
      setDetailPrefix(full as PrefixWithRequirement);
      setDetailHolders(holders.results);
      setDetailGrants(grantHistory);
      usernameCacheService.prefetchUsernames([
        ...holders.results.map((h) => h.user_uuid),
        ...grantHistory.map((g) => g.member_uuid),
      ]);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDeleteEntry = async () => {
    if (!entryToDelete) return;
    setDeletingEntry(true);
    try {
      await eventTeamApiService.deletePointsEntry(entryToDelete.id);
      setEntryToDelete(null);
      await loadLedger(ledgerPage);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete ledger entry');
    } finally {
      setDeletingEntry(false);
    }
  };

  const handleRevoke = async () => {
    if (!grantToRevoke) return;
    setRevoking(true);
    try {
      await eventTeamApiService.revokePrefixGrant(grantToRevoke.id);
      setGrantToRevoke(null);
      await loadGrants(grantsPage);
      if (detailPrefix) await openPrefixDetail(detailPrefix);
    } catch (err) {
      if (err instanceof EventTeamApiError && err.status === 409) {
        alert('This grant was already revoked.');
        setGrantToRevoke(null);
        await loadGrants(grantsPage);
      } else {
        alert(err instanceof Error ? err.message : 'Failed to revoke grant');
      }
    } finally {
      setRevoking(false);
    }
  };

  const handleLookup = async (uuidOverride?: string) => {
    const uuid = uuidOverride ?? lookupPlayer?.uuid;
    if (!uuid) return;
    const request = ++lookupRequest.current;
    setLookupLoading(true);
    setLookupError(null);
    setLookupResult(null);
    setLookupGrantPrefixId('');
    try {
      const res = await eventTeamApiService.getPlayerPrefixes(uuid);
      if (request === lookupRequest.current) setLookupResult(res);
    } catch (err) {
      if (request === lookupRequest.current) setLookupError(err instanceof Error ? err.message : 'Failed to look up player');
    } finally {
      if (request === lookupRequest.current) setLookupLoading(false);
    }
  };

  /** Lazy-load the full catalog + requirement overlays for the lookup panel's "Apply prefix" picker. */
  const ensureLookupGrantOptions = async () => {
    if (lookupGrantOptions) return;
    try {
      const [res, reqs] = await Promise.all([
        eventTeamApiService.listPrefixes({ limit: 200 }),
        eventTeamApiService.listPrefixRequirements().catch(() => [] as EventPrefixRequirement[]),
      ]);
      setLookupGrantOptions([...res.results].sort((a, b) => a.priority - b.priority));
      if (reqs.length > 0) setRequirements(reqs);
    } catch {
      setLookupGrantOptions([]);
    }
  };

  const afterGrant = () => {
    // Refresh whatever's visible (guide §2: refresh player prefixes + leaderboard).
    if (subTab === 'eligibility') void loadEligibility();
    if (subTab === 'grants') void loadGrants(grantsPage);
    if (subTab === 'leaderboard') void loadLeaderboard();
    if (detailPrefix) void openPrefixDetail(detailPrefix);
    if (lookupResult) void handleLookup();
  };

  // ── Render helpers ──────────────────────────────────────────────────────

  const renderPager = (page: number, total: number, pageSize: number, onPage: (p: number) => void) => {
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    if (totalPages <= 1) return null;
    return (
      <div className="flex items-center justify-center gap-3">
        <button
          type="button"
          onClick={() => onPage(page - 1)}
          disabled={page <= 1 || loading}
          className="btn-icon"
          title="Previous page"
          aria-label="Previous page"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-xs text-glass-400 tabular-nums">
          Page {page} of {totalPages}
        </span>
        <button
          type="button"
          onClick={() => onPage(page + 1)}
          disabled={page >= totalPages || loading}
          className="btn-icon"
          title="Next page"
          aria-label="Next page"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    );
  };

  const errorCard = (msg: string | null) =>
    msg ? (
      <div className="alert alert-danger" role="alert">
        <AlertCircle />
        <span className="flex-1">{msg}</span>
      </div>
    ) : null;

  return (
    <div className="space-y-4">
      {/* Sub-tab bar */}
      <div className="flex flex-wrap items-center gap-3">
      <select aria-label="Player rewards view" className="glass-select w-full sm:hidden" value={['player', 'leaderboard', 'ledger'].includes(subTab) ? subTab : ''} onChange={(e) => setSubTab(e.target.value as SubTab)}>
        <option value="" disabled>Player rewards</option>
        {['player', 'leaderboard', 'ledger'].map((id) => SUB_TABS.find((t) => t.id === id)!).map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
      </select>
      <div className="tabs flex-wrap hidden sm:flex" role="tablist" aria-label="Player rewards views">
        {['player', 'leaderboard', 'ledger'].map((id) => SUB_TABS.find((t) => t.id === id)!).map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={subTab === t.id}
              onClick={() => setSubTab(t.id)}
              className="tab min-h-[44px]"
              data-active={subTab === t.id ? 'true' : 'false'}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>
      <select aria-label="More reward tools" className="glass-select flex-1 min-w-0 sm:flex-none sm:w-auto" value={['catalog', 'eligibility', 'grants'].includes(subTab) ? subTab : ''} onChange={(e) => setSubTab(e.target.value as SubTab)}>
        <option value="" disabled>Prefix rewards…</option>
        {SUB_TABS.filter((t) => ['catalog', 'eligibility', 'grants'].includes(t.id)).map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
      </select>
      {perms.canPoints && <button data-tour="event-reward-award" type="button" className="btn-primary min-h-[44px]" onClick={() => { setAwardUuid(subTab === 'player' ? lookupPlayer?.uuid : undefined); setShowAward(true); }}><Award className="w-4 h-4" />Award points</button>}
      </div>

      {loading && (
        <div className="glass-card divide-y divide-glass-200/10 overflow-hidden" aria-busy="true">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3">
              <SkeletonLoader className="h-4 w-6" />
              <SkeletonLoader className="h-4 w-40" />
              <SkeletonLoader className="h-4 w-12 ml-auto" />
            </div>
          ))}
        </div>
      )}

      {/* ── Leaderboard ─────────────────────────────────────────────────── */}
      {subTab === 'leaderboard' && !loading && (
        <div className="space-y-4">
          {errorCard(leaderboardError)}
          <SectionHeader
            as="h3"
            title="Player points leaderboard"
            icon={Trophy}
            iconClassName="text-yellow-400"
            className="mb-0 flex-wrap"
          />

          {pointsStats && Object.keys(pointsStats).length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
              {Object.entries(pointsStats)
                .filter(([, v]) => typeof v === 'number' || typeof v === 'string')
                .slice(0, 6)
                .map(([key, value]) => (
                  <StatTile key={key} label={key === 'members_with_points' ? 'Players with points' : key.replace(/_/g, ' ')} value={String(value)} />
                ))}
            </div>
          )}

          {(leaderboard ?? []).length === 0 ? (
            <EmptyState icon={Trophy} title="No points awarded" />
          ) : (
            <div className="glass-card divide-y divide-glass-200/10 overflow-hidden">
              {(leaderboard ?? []).map((row, i) => (
                <button
                  key={row.member_uuid}
                  type="button"
                  onClick={() => void openMemberSummary(row.member_uuid)}
                  className="w-full flex items-center justify-between gap-2 px-4 py-2.5 hover:bg-glass-200/5 transition-colors text-left"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className={cn(
                        'w-7 text-center text-sm font-semibold tabular-nums flex-shrink-0',
                        i === 0 ? 'text-yellow-400' : i === 1 ? 'text-gray-300' : i === 2 ? 'text-amber-600' : 'text-glass-500',
                      )}
                    >
                      {i + 1}
                    </span>
                    <PlayerProfile uuid={row.member_uuid} name={row.display_name ?? undefined} showUuid={false} inline />
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 text-xs text-glass-400 tabular-nums">
                    <span>{row.entries_count} entries</span>
                    {row.last_entry_at && <span>last {formatDistanceToNow(new Date(row.last_entry_at), { addSuffix: true })}</span>}
                    <span className="text-base font-semibold tabular-nums text-yellow-300">{row.total}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Ledger ──────────────────────────────────────────────────────── */}
      {subTab === 'ledger' && !loading && (
        <div className="space-y-4">
          {errorCard(ledgerError)}
          <div className="glass-card p-3 flex items-center gap-2 flex-wrap">
            <PlayerSearchField
              value={ledgerMemberPlayer}
              onChange={setLedgerMemberPlayer}
              compact
              className="w-64"
              placeholder="Filter by player…"
            />
            <input
              type="text"
              value={ledgerEventFilter}
              onChange={(e) => setLedgerEventFilter(e.target.value)}
              placeholder="Filter by event id"
              className="glass-input text-sm w-56 font-mono"
            />
            <select
              value={ledgerTypeFilter}
              onChange={(e) => setLedgerTypeFilter(e.target.value)}
              className="glass-select text-sm capitalize"
              aria-label="Filter by entry type"
            >
              <option value="">All entry types</option>
              {['award', 'deduction', 'adjustment', 'prefix_redemption'].map((t) => (
                <option key={t} value={t}>
                  {t.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
            <button type="button" onClick={() => void loadLedger(1)} className="glass-button">
              Apply
            </button>
            <span className="text-xs text-glass-400 ml-auto tabular-nums">{ledgerTotal} entries</span>
          </div>

          {ledger.length === 0 ? (
            <EmptyState icon={Award} title="No ledger entries match" />
          ) : (
            <div className="glass-card divide-y divide-glass-200/10 overflow-hidden">
              {ledger.map((entry) => (
                <div
                  key={entry.id}
                  className="flex items-center justify-between gap-2 px-4 py-2.5 flex-wrap"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-wrap">
                    <span className={cn('text-sm font-semibold tabular-nums', entry.delta >= 0 ? 'text-green-300' : 'text-red-300')}>
                      {entry.delta >= 0 ? `+${entry.delta}` : entry.delta}
                    </span>
                    <span
                      className={cn(
                        'badge capitalize',
                        ENTRY_TYPE_CHIP[entry.entry_type] ?? ENTRY_TYPE_CHIP.adjustment,
                      )}
                    >
                      {entry.entry_type.replace(/_/g, ' ')}
                    </span>
                    <PlayerProfile uuid={entry.member_uuid} showUuid={false} inline />
                    <span className="text-sm text-glass-200 truncate">{entry.reason}</span>
                    {entry.event_id && (
                      <button
                        onClick={() => navigate(`/event-team/events/${entry.event_id}`)}
                        className="text-[11px] text-accent-300 hover:text-accent-200 hover:underline underline-offset-2"
                      >
                        → event
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[11px] text-glass-500 inline-flex items-center gap-1 tabular-nums">
                      by <CreatorProfile creatorId={entry.awarded_by} inline /> ·{' '}
                      {format(new Date(entry.created_at), 'MMM d, HH:mm')}
                    </span>
                    {perms.canPoints && (
                      <button
                        type="button"
                        onClick={() => setEntryToDelete(entry)}
                        className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                        title="Delete entry (correction)"
                        aria-label="Delete ledger entry"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {renderPager(ledgerPage, ledgerTotal, LEDGER_PAGE_SIZE, (p) => void loadLedger(p))}
        </div>
      )}

      {/* ── Prefix catalog ──────────────────────────────────────────────── */}
      {subTab === 'catalog' && !loading && (
        <div className="space-y-4">
          {errorCard(catalogError)}
          <div className="alert alert-info" role="note">
            <Info />
            <p className="flex-1 text-xs">Read-only live game data. Admins can set point requirements.</p>
          </div>
          <div className="glass-card p-3 flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
              <input
                type="text"
                value={prefixSearch}
                onChange={(e) => setPrefixSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void loadCatalog(1);
                }}
                placeholder="Search prefixes…"
                className="glass-input w-full pl-9"
              />
            </div>
            <div className="segmented" role="tablist" aria-label="Prefix category">
              <button
                type="button"
                role="tab"
                aria-selected={prefixCategory === ''}
                onClick={() => setPrefixCategory('')}
                className="segmented-item segmented-item-sm"
                data-active={prefixCategory === '' ? 'true' : 'false'}
              >
                All
              </button>
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  role="tab"
                  aria-selected={prefixCategory === c}
                  onClick={() => setPrefixCategory(c)}
                  className="segmented-item segmented-item-sm capitalize"
                  data-active={prefixCategory === c ? 'true' : 'false'}
                >
                  {c}
                </button>
              ))}
            </div>
            <button type="button" onClick={() => void loadCatalog(1)} className="glass-button">
              Apply
            </button>
          </div>

          {prefixes.length === 0 ? (
            <EmptyState icon={ShieldCheck} title="No prefixes match" />
          ) : (
            <div className="glass-card divide-y divide-glass-200/10 overflow-hidden">
              {prefixes.map((prefix) => {
                const req = requirementFor(prefix.id);
                return (
                  <button
                    key={prefix.id}
                    type="button"
                    onClick={() => void openPrefixDetail(prefix)}
                    className="w-full flex items-center justify-between gap-2 px-4 py-2.5 hover:bg-glass-200/5 transition-colors text-left flex-wrap"
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                      <PrefixBadge prefixJson={prefix.prefix_json} name={prefix.name} />
                      <span className="text-sm text-glass-300">{prefix.name}</span>
                      <span className="badge badge-neutral capitalize">{prefix.category}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 text-[11px] text-glass-500 tabular-nums">
                      {req?.points_threshold != null && (
                        <span className="text-cyan-300">≥ {req.points_threshold} pts</span>
                      )}
                      {req?.points_cost != null && <span className="text-violet-300">cost {req.points_cost}</span>}
                      <span>priority {prefix.priority}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {renderPager(prefixPage, prefixTotal, 50, (p) => void loadCatalog(p))}
        </div>
      )}

      {/* ── Eligibility ─────────────────────────────────────────────────── */}
      {subTab === 'eligibility' && !loading && (
        <div className="space-y-4">
          {errorCard(eligibilityError)}
          <SectionHeader
            as="h3"
            title="Prefix eligibility"
            icon={BadgeCheck}
            iconClassName="text-green-400"
            description="Players whose points meet a prefix threshold."
            className="mb-0 flex-wrap"
            actions={
              <>
                <input
                  type="number"
                  value={eligibilityPrefixFilter}
                  onChange={(e) => setEligibilityPrefixFilter(e.target.value)}
                  placeholder="Prefix id (optional)"
                  className="glass-input text-sm w-44"
                  aria-label="Filter by prefix id"
                />
                <button type="button" onClick={() => void loadEligibility()} className="glass-button">
                  Apply
                </button>
              </>
            }
          />

          {eligibility.length === 0 ? (
            <EmptyState icon={BadgeCheck} title="Nobody meets a threshold" />
          ) : (
            <div className="glass-card divide-y divide-glass-200/10 overflow-hidden">
              {eligibility.map((row) => (
                <div
                  key={`${row.member_uuid}-${row.prefix_id}`}
                  className="flex items-center justify-between gap-2 px-4 py-2.5 flex-wrap"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-wrap">
                    <PlayerProfile uuid={row.member_uuid} name={row.display_name ?? undefined} showUuid={false} inline />
                    <span className="text-sm font-semibold tabular-nums text-yellow-300">{row.total_points} pts</span>
                    <span className="text-glass-500 text-xs">→</span>
                    <span className="text-sm text-glass-200">{row.prefix_name}</span>
                    <span className="text-[11px] text-cyan-300 tabular-nums">(needs {row.points_threshold})</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {row.already_holds ? (
                      <span className="badge badge-neutral">
                        <BadgeCheck className="w-3 h-3" />
                        already holds
                      </span>
                    ) : perms.canPoints ? (
                      <button
                        onClick={() => {
                          const prefix: Prefix = {
                            id: row.prefix_id,
                            name: row.prefix_name,
                            created: '',
                            priority: 0,
                            prefix_json: null,
                            category: '',
                          };
                          setGrantTarget({
                            prefix,
                            requirement: requirementFor(row.prefix_id),
                            presetUuid: row.member_uuid,
                          });
                        }}
                        className="glass-button-sm"
                      >
                        <ShieldCheck className="w-3.5 h-3.5" />
                        Grant
                      </button>
                    ) : (
                      <span className="text-[11px] text-glass-500">eligible</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Grants history ──────────────────────────────────────────────── */}
      {subTab === 'grants' && !loading && (
        <div className="space-y-4">
          {errorCard(grantsError)}
          <SectionHeader
            as="h3"
            title="Grant history"
            icon={ShieldCheck}
            iconClassName="text-green-400"
            className="mb-0 flex-wrap"
            actions={
              <select
                value={grantsStatusFilter}
                onChange={(e) => setGrantsStatusFilter(e.target.value as 'active' | 'revoked' | '')}
                className="glass-select md:w-44 capitalize"
                aria-label="Filter grants by status"
              >
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="revoked">Revoked</option>
              </select>
            }
          />

          {grants.length === 0 ? (
            <EmptyState icon={ShieldCheck} title="No grants recorded" />
          ) : (
            <div className="glass-card divide-y divide-glass-200/10 overflow-hidden">
              {grants.map((grant) => (
                <div
                  key={grant.id}
                  className="flex items-center justify-between gap-2 px-4 py-2.5 flex-wrap"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-wrap">
                    <PlayerProfile uuid={grant.member_uuid} showUuid={false} inline />
                    <span className="text-sm text-glass-200">prefix #{grant.prefix_id}</span>
                    <span
                      className={cn(
                        'badge badge-dot capitalize',
                        grant.status === 'active' ? 'badge-success' : 'badge-danger',
                      )}
                    >
                      {grant.status}
                    </span>
                    {grant.points_spent != null && grant.points_spent > 0 && (
                      <span className="text-[11px] text-violet-300">−{grant.points_spent} pts</span>
                    )}
                    {grant.notes && <span className="text-[11px] text-glass-500 italic truncate max-w-[220px]">{grant.notes}</span>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[11px] text-glass-500 inline-flex items-center gap-1 tabular-nums">
                      by <CreatorProfile creatorId={grant.granted_by} inline /> ·{' '}
                      {formatDistanceToNow(new Date(grant.granted_at), { addSuffix: true })}
                    </span>
                    {perms.canPoints && grant.status === 'active' && (
                      <button
                        type="button"
                        onClick={() => setGrantToRevoke(grant)}
                        className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                        title="Revoke (removes the live prefix immediately)"
                        aria-label="Revoke grant"
                      >
                        <ShieldOff className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {renderPager(grantsPage, grantsTotal, 50, (p) => void loadGrants(p))}
        </div>
      )}

      {/* ── Player lookup ───────────────────────────────────────────────── */}
      {subTab === 'player' && (
        <div className="space-y-4">
          <SectionHeader
            as="h3"
            title="Player rewards"
            icon={UserSearch}
            iconClassName="text-cyan-400"
            description="Find any player to see their event points, review rewards, or recognise their participation."
            className="mb-0"
          />
          <div data-tour="event-reward-player" className="flex items-center gap-2 flex-wrap">
            <PlayerSearchField
              value={lookupPlayer}
              onChange={(sel) => {
                setLookupPlayer(sel);
                if (sel) void handleLookup(sel.uuid);
                else {
                  lookupRequest.current++;
                  setLookupLoading(false);
                  setLookupResult(null);
                  setLookupError(null);
                }
              }}
              className="flex-1 max-w-md"
            />
            {lookupLoading && <Loader className="w-4 h-4 animate-spin text-accent-400" />}
          </div>
          {errorCard(lookupError)}
          {lookupResult && (
            <div className="glass-card p-4 md:p-5 space-y-4">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <PlayerProfile uuid={lookupResult.user_uuid} />
                <div className="text-right">
                  <p className="text-2xl font-semibold tabular-nums text-yellow-300">{lookupResult.event_points_total}</p>
                  <p className="eyebrow">event points</p>
                  <button type="button" className="glass-button-secondary mt-2 min-h-[44px]" onClick={() => void openMemberSummary(lookupResult.user_uuid)}>View points history</button>
                </div>
              </div>

              {/* Apply a prefix straight from the lookup — works for non-team players. */}
              {perms.canPoints && (
                <div className="subcard flex items-center gap-2 flex-wrap p-2.5">
                  <ShieldCheck className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <select
                    value={lookupGrantPrefixId}
                    onChange={(e) => setLookupGrantPrefixId(e.target.value)}
                    onFocus={() => void ensureLookupGrantOptions()}
                    onClick={() => void ensureLookupGrantOptions()}
                    className="glass-select text-sm flex-1 min-w-[200px]"
                    aria-label="Prefix to apply"
                  >
                    <option value="">
                      {lookupGrantOptions == null ? 'Select a prefix… (click to load catalog)' : 'Select a prefix…'}
                    </option>
                    {(lookupGrantOptions ?? []).map((p) => (
                      <option key={p.id} value={String(p.id)}>
                        {prefixPlainText(p.prefix_json) || p.name} ({p.name}, {p.category})
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => {
                      const prefix = (lookupGrantOptions ?? []).find((p) => String(p.id) === lookupGrantPrefixId);
                      if (!prefix) return;
                      setGrantTarget({
                        prefix,
                        requirement: requirementFor(prefix.id),
                        presetUuid: lookupResult.user_uuid,
                      });
                    }}
                    disabled={!lookupGrantPrefixId}
                    className="glass-button-sm"
                  >
                    <ShieldCheck className="w-3.5 h-3.5" />
                    Apply prefix
                  </button>
                </div>
              )}
              <div>
                <h4 className="eyebrow mb-2">
                  Live prefix holdings ({lookupResult.holdings.length})
                </h4>
                {lookupResult.holdings.length === 0 ? (
                  <p className="text-glass-400 text-sm">No live prefixes.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {lookupResult.holdings.map((h) => (
                      <PrefixBadge key={h.prefix_id} prefixJson={h.prefix_json} name={h.name} size="md" />
                    ))}
                  </div>
                )}
              </div>
              <div>
                <h4 className="eyebrow mb-2">
                  Event grant history ({lookupResult.event_grants.length})
                </h4>
                {lookupResult.event_grants.length === 0 ? (
                  <p className="text-glass-400 text-sm">No event-team grants.</p>
                ) : (
                  <div className="space-y-1.5">
                    {lookupResult.event_grants.map((g) => (
                      <div
                        key={g.id}
                        className="subcard flex items-center justify-between gap-2 px-3 py-1.5 flex-wrap text-sm"
                      >
                        <span className="text-glass-200">
                          prefix #{g.prefix_id}
                          {g.points_spent != null && g.points_spent > 0 && (
                            <span className="text-violet-300 text-xs ml-2">−{g.points_spent} pts</span>
                          )}
                        </span>
                        <span className="flex items-center gap-2">
                          <span
                            className={cn(
                              'badge badge-dot capitalize',
                              g.status === 'active' ? 'badge-success' : 'badge-danger',
                            )}
                          >
                            {g.status}
                          </span>
                          {perms.canPoints && g.status === 'active' && (
                            <button
                              type="button"
                              onClick={() => setGrantToRevoke(g)}
                              className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                              title="Revoke"
                              aria-label="Revoke grant"
                            >
                              <ShieldOff className="w-3 h-3" />
                            </button>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Prefix detail drawer ────────────────────────────────────────── */}
      {(detailPrefix || detailLoading) && (
        <div
          className="modal-overlay z-[9990]"
          onClick={() => setDetailPrefix(null)}
        >
          <div
            className="modal-panel max-w-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Prefix details"
          >
            {detailLoading || !detailPrefix ? (
              <div className="flex items-center justify-center py-10">
                <Loader className="w-7 h-7 animate-spin text-accent-400" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="modal-header">
                  <div className="flex items-center gap-3 flex-wrap min-w-0">
                    <PrefixBadge prefixJson={detailPrefix.prefix_json} name={detailPrefix.name} size="md" />
                    <div className="min-w-0">
                      <h3 className="modal-title truncate">{detailPrefix.name}</h3>
                      <p className="modal-subtitle tabular-nums">
                        #{detailPrefix.id} · {detailPrefix.category} · priority {detailPrefix.priority}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {perms.canAdmin && (
                      <button
                        type="button"
                        onClick={() =>
                          setRequirementTarget({ prefix: detailPrefix, requirement: detailPrefix.requirement ?? requirementFor(detailPrefix.id) })
                        }
                        className="glass-button-secondary btn-xs"
                      >
                        <Settings2 className="w-3.5 h-3.5" />
                        Requirements
                      </button>
                    )}
                    {perms.canPoints && (
                      <button
                        type="button"
                        onClick={() =>
                          setGrantTarget({
                            prefix: detailPrefix,
                            requirement: detailPrefix.requirement ?? requirementFor(detailPrefix.id),
                          })
                        }
                        className="glass-button-sm"
                      >
                        <ShieldCheck className="w-3.5 h-3.5" />
                        Grant
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setDetailPrefix(null)}
                    className="modal-close"
                    title="Close"
                    aria-label="Close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {(detailPrefix.requirement ?? requirementFor(detailPrefix.id)) && (
                  <div className="subcard p-3 flex items-center gap-4 flex-wrap text-sm tabular-nums">
                    {(() => {
                      const req = detailPrefix.requirement ?? requirementFor(detailPrefix.id);
                      return (
                        <>
                          <span className="text-cyan-300">
                            Threshold: {req?.points_threshold ?? '—'} pts
                          </span>
                          <span className="text-violet-300">Cost: {req?.points_cost ?? '—'} pts</span>
                          {req?.notes && <span className="text-glass-400 italic text-xs">{req.notes}</span>}
                        </>
                      );
                    })()}
                  </div>
                )}

                <div>
                  <h4 className="eyebrow mb-2">
                    Live holders ({detailHolders?.length ?? 0})
                  </h4>
                  {(detailHolders ?? []).length === 0 ? (
                    <p className="text-glass-400 text-sm">Nobody holds this prefix right now.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {(detailHolders ?? []).map((h) => (
                        <span
                          key={h.user_uuid}
                          className="chip gap-1.5 px-2 py-1"
                        >
                          <PlayerProfile uuid={h.user_uuid} showUuid={false} inline />
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="eyebrow mb-2">
                    Event grant history ({detailGrants?.length ?? 0})
                  </h4>
                  {(detailGrants ?? []).length === 0 ? (
                    <p className="text-glass-400 text-sm">No grants recorded through the event system.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {(detailGrants ?? []).map((g) => (
                        <div
                          key={g.id}
                          className="subcard flex items-center justify-between gap-2 px-3 py-1.5 flex-wrap text-sm"
                        >
                          <span className="flex items-center gap-2 flex-wrap">
                            <PlayerProfile uuid={g.member_uuid} showUuid={false} inline />
                            <span
                              className={cn(
                                'badge badge-dot capitalize',
                                g.status === 'active' ? 'badge-success' : 'badge-danger',
                              )}
                            >
                              {g.status}
                            </span>
                            {g.points_spent != null && g.points_spent > 0 && (
                              <span className="text-[11px] text-violet-300">−{g.points_spent} pts</span>
                            )}
                          </span>
                          <span className="flex items-center gap-2">
                            <span className="text-[11px] text-glass-500">
                              {formatDistanceToNow(new Date(g.granted_at), { addSuffix: true })}
                            </span>
                            {perms.canPoints && g.status === 'active' && (
                              <button
                                type="button"
                                onClick={() => setGrantToRevoke(g)}
                                className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                                title="Revoke"
                                aria-label="Revoke grant"
                              >
                                <ShieldOff className="w-3 h-3" />
                              </button>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Member summary drawer ───────────────────────────────────────── */}
      {(memberSummary || memberSummaryLoading) && (
        <div
          className="modal-overlay z-[9990]"
          onClick={() => setMemberSummary(null)}
        >
          <div
            className="modal-panel max-w-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Player points summary"
          >
            {memberSummaryLoading || !memberSummary ? (
              <div className="flex items-center justify-center py-10">
                <Loader className="w-6 h-6 animate-spin text-accent-400" />
              </div>
            ) : (
              <div className="space-y-3">
                <div className="modal-header">
                  <PlayerProfile uuid={memberSummary.member_uuid} showUuid={false} />
                  <div className="text-right">
                    <span className="text-2xl font-semibold tabular-nums text-yellow-300">{memberSummary.total}</span>
                    <p className="eyebrow">points</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setMemberSummary(null)}
                    className="modal-close"
                    title="Close"
                    aria-label="Close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {(memberSummary.entries ?? []).length === 0 ? (
                  <p className="text-glass-400 text-sm">No entries.</p>
                ) : (
                  <div className="space-y-1.5">
                    {memberSummary.entries.map((entry) => (
                      <div
                        key={entry.id}
                        className="subcard flex items-center justify-between gap-2 px-3 py-1.5 text-sm flex-wrap"
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          <span className={cn('font-semibold tabular-nums', entry.delta >= 0 ? 'text-green-300' : 'text-red-300')}>
                            {entry.delta >= 0 ? `+${entry.delta}` : entry.delta}
                          </span>
                          <span className="text-glass-200 truncate">{entry.reason}</span>
                        </span>
                        <span className="text-[11px] text-glass-500 tabular-nums">
                          {format(new Date(entry.created_at), 'MMM d, yyyy')}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Modals ──────────────────────────────────────────────────────── */}
      {perms.canPoints && <AwardPointsModal isOpen={showAward} presetMemberUuid={awardUuid} onClose={() => { setShowAward(false); setAwardUuid(undefined); }} onSuccess={() => { if (subTab === 'leaderboard') void loadLeaderboard(); if (subTab === 'ledger') void loadLedger(ledgerPage); if (lookupPlayer) void handleLookup(); }} />}

      {grantTarget && (
        <GrantPrefixModal
          isOpen={!!grantTarget}
          prefix={grantTarget.prefix}
          requirement={grantTarget.requirement}
          presetUuid={grantTarget.presetUuid}
          onClose={() => setGrantTarget(null)}
          onSuccess={afterGrant}
        />
      )}

      {requirementTarget && (
        <PrefixRequirementModal
          isOpen={!!requirementTarget}
          prefix={requirementTarget.prefix}
          requirement={requirementTarget.requirement}
          onClose={() => setRequirementTarget(null)}
          onSuccess={() => {
            void loadCatalog(prefixPage);
            if (detailPrefix) void openPrefixDetail(detailPrefix);
          }}
        />
      )}

      {entryToDelete && (
        <ConfirmationModal
          isOpen={!!entryToDelete}
          onClose={() => setEntryToDelete(null)}
          onConfirm={() => void handleDeleteEntry()}
          title="Delete Ledger Entry"
          message={`Delete this ${entryToDelete.delta >= 0 ? '+' : ''}${entryToDelete.delta} entry ("${entryToDelete.reason}")? Totals are recomputed from the remaining entries.`}
          confirmText="Delete"
          isLoading={deletingEntry}
        />
      )}

      {grantToRevoke && (
        <ConfirmationModal
          isOpen={!!grantToRevoke}
          onClose={() => setGrantToRevoke(null)}
          onConfirm={() => void handleRevoke()}
          title="Revoke Prefix Grant"
          message="The prefix is removed in-game immediately. Grant history is kept."
          confirmText="Revoke"
          isLoading={revoking}
        />
      )}
    </div>
  );
};
